/**
 * Trouble Cards (Leech) widget — Wave 1.
 * Shows top cards the student keeps failing (≥4 lapses) so they can attack
 * weak content directly. No AI calls — pure stats from student_question_mastery.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, ChevronRight } from "lucide-react";

export default function TroubleCardsWidget({ limit = 5, onOpenCurve }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    let cancelled = false;
    api.get(`/student/trouble-cards?limit=${limit}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData({ trouble_cards: [] }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [limit]);

  if (loading) {
    return (
      <Card className="p-4 border-slate-200 flex items-center gap-2 text-slate-500 text-sm" data-testid="trouble-cards-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Scanning your trouble cards…
      </Card>
    );
  }

  const cards = data?.trouble_cards || [];
  if (cards.length === 0) {
    return (
      <Card className="p-4 border-slate-200 bg-emerald-50/30" data-testid="trouble-cards-empty">
        <div className="flex items-center gap-2">
          <span aria-hidden className="text-lg leading-none">✨</span>
          <div>
            <h3 className="font-display font-bold text-sm text-emerald-900">No trouble cards</h3>
            <p className="text-[12px] text-emerald-700/80 mt-0.5">
              Nothing has lapsed {data?.threshold || 4}+ times yet. Keep up the streak!
            </p>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-4 border-amber-200 bg-amber-50/40" data-testid="trouble-cards-widget">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display font-bold text-sm flex items-center gap-2">
          <span aria-hidden>🪤</span> Trouble Cards
          <Badge className="bg-amber-600 hover:bg-amber-700 text-white text-[10px] uppercase tracking-wide font-semibold">{cards.length}</Badge>
        </h3>
        <Button
          variant="ghost"
          size="sm"
          className="text-amber-800 hover:bg-amber-100 hover:text-amber-900 text-xs px-2"
          onClick={() => nav("/student/smart-review")}
          data-testid="trouble-cards-attack-btn"
        >
          Attack now <ChevronRight className="w-3 h-3 ml-0.5" />
        </Button>
      </div>
      <ul className="space-y-2">
        {cards.map((c) => (
          <li
            key={c.question_id}
            className="flex items-start gap-3 p-2 rounded-md hover:bg-amber-100/60 transition-colors cursor-pointer"
            onClick={() => onOpenCurve?.(c.question_id)}
            data-testid={`trouble-card-${c.question_id}`}
          >
            <div className="text-amber-700 text-xs font-bold mt-0.5 w-8 text-center shrink-0">
              {c.lapses_count}×
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[12px] text-slate-800 line-clamp-2 leading-snug">
                {c.question_preview}
              </p>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                {c.subject_name && (
                  <span className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">{c.subject_name}</span>
                )}
                {c.module_name && (
                  <span className="text-[10px] text-slate-400">· {c.module_name}</span>
                )}
                <span className="text-[10px] text-rose-700 font-bold">{c.accuracy_percentage}% acc.</span>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}
